"use client";

import { useMemo } from "react";
import { useRouter } from "next/router";
import {
  isAvailableForCountry,
  resolveCurrentCountry,
  resolveGeoRecord,
} from "@/components/Templates/Main/GeoConditional";
import { RelatedProductsCarousel } from "@/components/Organisms/Product/ProductRange/RelatedProductsCarousel";
import { ShowcaseProductCard } from "@/components/Organisms/ProductsGallerySectionBrand/ShowcaseProductCard";
import { mapShowcaseEntryToSlide } from "./mapShowcaseEntryToSlide";
import type { ButtonMasterVariantProps } from "@/components/Atoms/Buttons";

type ProductsGalleryCarouselProps = {
  productsShowcase: any[];
  portfolioSlug: string;
  titleColor?: { color?: { hex?: string; cssRgb?: string } };
  buttonStyle?: ButtonMasterVariantProps["type"];
};

export function ProductsGalleryCarousel({
  productsShowcase,
  portfolioSlug,
  titleColor,
  buttonStyle,
}: ProductsGalleryCarouselProps) {
  const { locale, query } = useRouter();

  const slides = useMemo(
    () =>
      productsShowcase
        .map(mapShowcaseEntryToSlide)
        .filter(Boolean) as any[],
    [productsShowcase]
  );

  // Per ProductConf `geo` — hide unavailable products before building the carousel.
  const visibleSlides = useMemo(
    () =>
      slides.filter((slide) =>
        isAvailableForCountry(
          resolveGeoRecord(slide?.geo),
          resolveCurrentCountry(query.country),
          query.ignoregeo
        )
      ),
    [slides, query.country, query.ignoregeo]
  );

  const carouselData = useMemo(
    () => ({
      productDetailTextColor: titleColor,
      relatedTextColor: titleColor,
      relatedTitleColor: titleColor,
      addRelatedTitleUnderline: false,
      relatedCtaLabel: { label: "seeMore" },
    }),
    [titleColor]
  );

  const navStyle = {
    "--color": titleColor?.color?.hex,
    "--relatedcolor": titleColor?.color?.hex,
    "--iconColor": titleColor?.color?.hex,
  } as React.CSSProperties;

  if (visibleSlides.length === 0) return null;

  if (visibleSlides.length === 1) {
    return (
      <div className="container mx-auto my-8 flex justify-center">
        <ShowcaseProductCard
          variant="single"
          entry={visibleSlides[0]}
          locale={locale}
          portfolioSlug={portfolioSlug}
          textColor={titleColor?.color}
          buttonStyle={buttonStyle}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto my-8 w-full max-w-[96rem] px-0 md:px-8">
      <RelatedProductsCarousel
        slides={visibleSlides}
        loop={visibleSlides.length > 1}
        showNav={visibleSlides.length > 1}
        data={carouselData}
        navColor={titleColor?.color}
        navStyle={navStyle}
        locale={locale}
        portfolioSlug={portfolioSlug}
        cardVariant="showcase"
        carouselLayout="gallery"
        showcaseTextColor={titleColor?.color}
        showcaseButtonStyle={buttonStyle}
        showBrandLabel
      />
    </div>
  );
}
